import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthLayout from "@/pages/auth/components/AuthLayout";
import { supabase } from "@/lib/supabase";

// ── Stripe type declarations ──────────────────────────────────────────────────
declare global {
  interface Window {
    Stripe?: (key: string) => StripeInstance;
  }
}

interface StripeInstance {
  elements: (options: Record<string, unknown>) => StripeElements;
  confirmSetup: (options: Record<string, unknown>) => Promise<{ error?: { message: string } }>;
  retrieveSetupIntent: (clientSecret: string) => Promise<{
    setupIntent?: { status: string; payment_method?: string };
    error?: { message: string };
  }>;
}

interface StripeElements {
  create: (type: string, options?: Record<string, unknown>) => StripeElement;
  submit: () => Promise<{ error?: { message: string } }>;
}

interface StripeElement {
  mount: (el: HTMLElement) => void;
  destroy: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function loadStripeScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById("stripe-js")) { resolve(); return; }
    const script = document.createElement("script");
    script.id = "stripe-js";
    script.src = "https://js.stripe.com/v3/";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Stripe.js"));
    document.body.appendChild(script);
  });
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PaymentSetupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Detect return from Stripe redirect
  const setupIntentSecret = searchParams.get("setup_intent_client_secret");
  const redirectStatus = searchParams.get("redirect_status");

  const [phase, setPhase] = useState<"loading" | "form" | "confirming" | "success" | "error">(
    setupIntentSecret ? "confirming" : "loading"
  );
  const [errorMsg, setErrorMsg] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [last4, setLast4] = useState<string | null>(null);

  const paymentElementRef = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<StripeInstance | null>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const paymentElementInstanceRef = useRef<StripeElement | null>(null);

  // ── Phase: confirming (returned from Stripe redirect) ──────────────────────
  useEffect(() => {
    if (phase !== "confirming" || !setupIntentSecret) return;

    const confirm = async () => {
      try {
        await loadStripeScript();
        const pubKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
        if (!pubKey) throw new Error("Stripe publishable key not configured");

        const stripe = window.Stripe!(pubKey);
        const { setupIntent, error } = await stripe.retrieveSetupIntent(setupIntentSecret);

        if (error) throw new Error(error.message);

        if (setupIntent?.status === "succeeded") {
          // Save payment method ID to DB
          const pmId = setupIntent.payment_method;
          if (pmId) {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              await supabase
                .from("installers")
                .update({ stripe_payment_method_id: pmId })
                .eq("id", session.user.id);
            }
          }
          sessionStorage.setItem("payment_setup", "true");
          setPhase("success");
        } else if (redirectStatus === "failed") {
          throw new Error("Payment setup failed. Please try again.");
        } else {
          throw new Error(`Unexpected status: ${setupIntent?.status}`);
        }
      } catch (e) {
        setErrorMsg(e instanceof Error ? e.message : "Something went wrong");
        setPhase("error");
      }
    };

    confirm();
  }, [phase, setupIntentSecret, redirectStatus]);

  // ── Phase: loading → form ──────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "loading") return;

    const init = async () => {
      try {
        // Load Stripe.js
        await loadStripeScript();
        const pubKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
        if (!pubKey) throw new Error("Stripe publishable key not configured. Add VITE_STRIPE_PUBLISHABLE_KEY to your .env file.");

        // Get auth session
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error("Not authenticated. Please log in again.");

        // Create Setup Intent
        const res = await fetch(
          "https://ukfoujowiwsrrmajzajy.supabase.co/functions/v1/create-setup-intent",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
          }
        );
        const data = await res.json();
        if (!res.ok || !data.client_secret) {
          throw new Error(data.error || "Failed to initialize payment form");
        }

        setClientSecret(data.client_secret);
        setPhase("form");
      } catch (e) {
        setErrorMsg(e instanceof Error ? e.message : "Failed to initialize payment");
        setPhase("error");
      }
    };

    init();
  }, [phase]);

  // ── Mount Stripe Payment Element once clientSecret is ready ───────────────
  useEffect(() => {
    if (phase !== "form" || !clientSecret || !paymentElementRef.current) return;

    const pubKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    if (!pubKey || !window.Stripe) return;

    // Destroy previous instance if any
    if (paymentElementInstanceRef.current) {
      paymentElementInstanceRef.current.destroy();
      paymentElementInstanceRef.current = null;
    }

    const stripe = window.Stripe(pubKey);
    stripeRef.current = stripe;

    const elements = stripe.elements({
      clientSecret,
      appearance: {
        theme: "stripe",
        variables: {
          colorPrimary: "#16a34a",
          colorBackground: "#ffffff",
          colorText: "#374151",
          colorDanger: "#ef4444",
          fontFamily: "Inter, system-ui, sans-serif",
          borderRadius: "8px",
          spacingUnit: "4px",
        },
      },
    });
    elementsRef.current = elements;

    const paymentElement = elements.create("payment", {
      layout: "tabs",
      defaultValues: { billingDetails: { address: { country: "US" } } },
    });
    paymentElementInstanceRef.current = paymentElement;
    paymentElement.mount(paymentElementRef.current);

    return () => {
      paymentElement.destroy();
      paymentElementInstanceRef.current = null;
    };
  }, [phase, clientSecret]);

  // ── Submit handler ─────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripeRef.current || !elementsRef.current) return;

    setPhase("confirming");
    setErrorMsg("");

    try {
      // Validate the element first
      const { error: submitError } = await elementsRef.current.submit();
      if (submitError) throw new Error(submitError.message);

      // Confirm the setup — Stripe will redirect back to this page with ?setup_intent_client_secret=...
      const { error } = await stripeRef.current.confirmSetup({
        elements: elementsRef.current,
        confirmParams: {
          return_url: `${window.location.origin}/payment-setup`,
        },
      });

      // If we get here without redirect, it's an error
      if (error) throw new Error(error.message);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Something went wrong. Please try again.");
      setPhase("form");
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (phase === "loading") {
    return (
      <AuthLayout title="Add Payment Method" subtitle="Loading secure payment form..." step={3} totalSteps={4}>
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <i className="ri-loader-4-line animate-spin text-3xl text-[#16a34a] mb-3"></i>
          <span className="text-sm">Initializing secure payment...</span>
        </div>
      </AuthLayout>
    );
  }

  if (phase === "confirming") {
    return (
      <AuthLayout title="Verifying Payment Method" subtitle="Please wait while we confirm your card..." step={3} totalSteps={4}>
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <i className="ri-loader-4-line animate-spin text-3xl text-[#16a34a] mb-3"></i>
          <span className="text-sm">Confirming with Stripe...</span>
        </div>
      </AuthLayout>
    );
  }

  if (phase === "success") {
    return (
      <AuthLayout title="Payment Method Saved!" subtitle="You're all set. Your dashboard is now unlocked." step={3} totalSteps={4}>
        <div className="space-y-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <i className="ri-check-double-line text-[#16a34a] text-3xl"></i>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Payment Method Saved!</h3>
            <p className="text-gray-500 text-sm">Your card has been securely saved. You can now purchase leads from the dashboard.</p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 text-left space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center">
                <i className="ri-bank-card-line text-white"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {last4 ? `Card ending in ${last4}` : "Card saved successfully"}
                </p>
                <p className="text-xs text-gray-500">Ready for purchases</p>
              </div>
              <span className="ml-auto inline-flex items-center gap-1 bg-green-100 text-green-700 px-2.5 py-0.5 rounded-full text-xs font-semibold">
                <i className="ri-check-line"></i> Active
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="w-full bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold py-3 rounded-lg text-sm transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-dashboard-line mr-2"></i>Go to My Dashboard
            </button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (phase === "error") {
    return (
      <AuthLayout title="Add Payment Method" subtitle="There was a problem setting up your payment." step={3} totalSteps={4}>
        <div className="space-y-5">
          <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
            <i className="ri-error-warning-line text-red-500 text-3xl mb-3 block"></i>
            <p className="text-sm font-semibold text-red-700 mb-1">Setup Failed</p>
            <p className="text-xs text-red-600">{errorMsg}</p>
          </div>
          <button
            type="button"
            onClick={() => { setErrorMsg(""); setPhase("loading"); }}
            className="w-full bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold py-3 rounded-lg text-sm transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line mr-2"></i>Try Again
          </button>
        </div>
      </AuthLayout>
    );
  }

  // phase === "form"
  return (
    <AuthLayout
      title="Add Payment Method"
      subtitle="Your card is only charged when you purchase a lead. No hidden fees."
      step={3}
      totalSteps={4}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Security badge */}
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-xs text-gray-600">
          <i className="ri-lock-line text-[#16a34a]"></i>
          <span>Payments secured by <strong>Stripe</strong>. We never store your card details.</span>
        </div>

        {errorMsg && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex items-start gap-2">
            <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Stripe Payment Element container */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Card Details</label>
          <div
            ref={paymentElementRef}
            className="w-full rounded-lg border border-gray-200 bg-white min-h-[120px] p-1"
          />
          <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
            <i className="ri-shield-check-line"></i>
            PCI compliant · Card data never touches our servers
          </p>
        </div>

        {/* Info note */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-700 flex items-start gap-2">
          <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
          <span>A <strong>$0 authorization hold</strong> may appear temporarily to verify your card. You will only be charged when you purchase a lead.</span>
        </div>

        <button
          type="submit"
          disabled={!clientSecret}
          className="w-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-semibold py-3 rounded-lg text-sm transition-all whitespace-nowrap cursor-pointer"
        >
          <i className="ri-lock-line mr-2"></i>Save Payment Method
        </button>

        <p className="text-center text-xs text-gray-400">
          <i className="ri-shield-check-line mr-1"></i>
          256-bit SSL encryption · PCI DSS compliant · Powered by Stripe
        </p>
      </form>
    </AuthLayout>
  );
}
